import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { 
  Megaphone, 
  Bell, 
  Calendar, 
  Pin, 
  ChevronLeft, 
  ChevronRight,
  Check
} from "lucide-react";
import { useToast } from "../../../components/admin/ToastContext";
import { get, put } from "../../../services/lewooApi";
import { PageSkeleton } from "../../../components/admin/LoadingSkeleton";

const CAT_COLORS = {
  General: "bg-slate-50 text-slate-600 border-slate-200",
  HR: "bg-blue-50 text-blue-700 border-blue-200",
  IT: "bg-violet-50 text-violet-700 border-violet-200",
  Finance: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const CAT_LABELS = { general: "General", hr: "HR", it: "IT", finance: "Finance" };

const FILTER_KEYS = {
  All: "ess.announcements.catAll",
  Unread: "ess.announcements.catUnread",
  Pinned: "ess.announcements.catPinned",
};

const ESSAnnouncementsPage = () => {
  const intl = useIntl();
  const addToast = useToast();
  const [announcements, setAnnouncements] = useState([]);
  const [activeCat, setActiveCat] = useState("All");
  const [loaded, setLoaded] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  useEffect(() => {
    const load = async () => {
      try {
        const result = await get("/api/announcements?is_published=true&limit=100");
        const rows = result?.data || [];
        const now = new Date();
        const mapped = rows.map((r) => {
          const dateKey = r.date_start ? String(r.date_start).split("T")[0] : null;
          const d = dateKey ? new Date(dateKey) : null;
          return {
            id: r.lewoo_uid ?? r.id,
            title: r.name || intl.formatMessage({ id: "ess.announcements.fallbackTitle" }),
            preview: r.body || "—",
            date: d && !isNaN(d) ? d.toLocaleDateString(intl.locale) : "—",
            category: CAT_LABELS[r.category] || "General",
            unread: !(r.read || false),
            pinned: r.is_pinned || false,
            isThisMonth: d && !isNaN(d) && d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth(),
          };
        });
        setAnnouncements(mapped);
      } catch (_) {
        setAnnouncements([]);
      }
      setLoaded(true);
    };
    load();
  }, [intl]);

  const filtered = useMemo(() => {
    if (activeCat === "All") return announcements;
    if (activeCat === "Unread") return announcements.filter((a) => a.unread);
    if (activeCat === "Pinned") return announcements.filter((a) => a.pinned);
    return announcements.filter((a) => a.category === activeCat);
  }, [activeCat, announcements]);

  const CATEGORIES = useMemo(() => {
    const cats = ["All", "Unread", "Pinned"];
    const unique = [...new Set(announcements.map((a) => a.category))];
    return [...cats, ...unique];
  }, [announcements]);

  const catLabel = (cat) =>
    FILTER_KEYS[cat]
      ? intl.formatMessage({ id: FILTER_KEYS[cat] })
      : cat;

  const catCount = (cat) => {
    if (cat === "All") return "";
    if (cat === "Unread") return announcements.filter((a) => a.unread).length;
    if (cat === "Pinned") return announcements.filter((a) => a.pinned).length;
    return announcements.filter((a) => a.category === cat).length;
  };

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => { setPage(1); }, [activeCat]);

  const handleMarkRead = async (item) => {
    if (item.id == null) return;
    try {
      await put(`/api/announcements/${item.id}/read`);
      setAnnouncements(prev => prev.map(a => a.id === item.id ? { ...a, unread: false } : a));
      addToast(intl.formatMessage({ id: "ess.announcements.markedRead" }), "success");
    } catch (_) {
      addToast(intl.formatMessage({ id: "ess.common.submitError" }), "error");
    }
  };

  if (!loaded) return <PageSkeleton />;

  const stats = [
    { labelId: "ess.announcements.total", value: String(announcements.length), subId: "ess.announcements.subTotal", icon: Megaphone, color: "text-blue-600 bg-blue-100" },
    { labelId: "ess.announcements.unread", value: String(announcements.filter((a) => a.unread).length), subId: "ess.announcements.subNew", icon: Bell, color: "text-amber-600 bg-amber-100" },
    { labelId: "ess.announcements.thisMonth", value: String(announcements.filter((a) => a.isThisMonth).length), subId: "ess.announcements.subTotal", icon: Calendar, color: "text-emerald-600 bg-emerald-100" },
    { labelId: "ess.announcements.pinned", value: String(announcements.filter((a) => a.pinned).length), subId: "ess.announcements.subImportant", icon: Pin, color: "text-rose-600 bg-rose-100" },
  ];

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {intl.formatMessage({ id: "ess.announcements.title" })}
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          {intl.formatMessage({ id: "ess.announcements.subtitle" })}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(({ labelId, value, subId, icon: Icon, color }) => (
          <div key={labelId} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm transition-all hover:shadow-md">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-gray-500">{intl.formatMessage({ id: labelId })}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
                <p className="text-xs text-gray-400 mt-1">{intl.formatMessage({ id: subId })}</p>
              </div>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
                <Icon size={20} className={color.split(' ')[0]} />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="border-b border-gray-100">
          <div className="flex items-center gap-1 px-4 pt-3 overflow-x-auto">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCat(cat)}
                className={`px-3 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  cat === activeCat
                    ? "border-emerald-500 text-emerald-700"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                {catLabel(cat)}
                {cat !== "All" && (
                  <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">{catCount(cat)}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="divide-y divide-gray-100">
          {paginated.length === 0 ? (
            <div className="py-12 text-center text-gray-400">
              {intl.formatMessage({ id: "ess.announcements.empty" })}
            </div>
          ) : (
            paginated.map((ann) => (
              <div key={String(ann.id ?? ann.title)} className={`p-5 hover:bg-gray-50/80 transition-colors ${ann.unread ? "bg-blue-50/30" : ""}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {ann.pinned && <Pin size={14} className="text-rose-500 rotate-45" />}
                      {ann.unread && <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />}
                      <h4 className="text-sm font-semibold text-gray-900">{ann.title}</h4>
                    </div>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${CAT_COLORS[ann.category] || "bg-gray-50 text-gray-600 border-gray-200"}`}>
                        {ann.category}
                      </span>
                      <span className="text-xs text-gray-400">{ann.date}</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-2 line-clamp-2">{ann.preview}</p>
                  </div>
                  <div className="flex flex-col gap-2 flex-shrink-0 mt-1">
                    {ann.unread && (
                      <button onClick={() => handleMarkRead(ann)} className="inline-flex items-center text-xs text-emerald-600 hover:text-emerald-800 font-medium whitespace-nowrap">
                        <Check size={14} className="mr-1" /> {intl.formatMessage({ id: "ess.announcements.markRead" })}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50/30">
            <span className="text-xs text-gray-500">
              {intl.formatMessage(
                { id: "ess.common.pagination" },
                { count: filtered.length, page, total: totalPages }
              )}
            </span>
            <div className="flex gap-1">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                className="p-1.5 rounded border border-gray-200 bg-white disabled:opacity-30 hover:bg-gray-50 transition-colors"
              ><ChevronLeft size={16} /></button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded border transition-colors ${
                    p === page ? "bg-emerald-500 text-white border-emerald-500" : "border-gray-200 bg-white hover:bg-gray-50"
                  }`}
                >{p}</button>
              ))}
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                className="p-1.5 rounded border border-gray-200 bg-white disabled:opacity-30 hover:bg-gray-50 transition-colors"
              ><ChevronRight size={16} /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ESSAnnouncementsPage;
